import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Download, ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';

const money = (n) => `$${Number(n || 0).toFixed(2).replace(/\.00$/, '')}`;

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/** Today, and three months back — the widest range the server will export. */
const defaultRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - 3);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

/**
 * The select's value back into something the API can take.
 *
 * Most entities are "type:id". The unlinked group has no id — it is defined by
 * what it lacks — so it travels as its type alone and becomes a flag.
 */
const parseEntity = (key) => {
  if (!key) return undefined;
  const [type, id] = key.split(':');
  return id ? { type, id } : { type };
};

/**
 * The host's ledger: what came in, what went out, and the balance after each.
 *
 * The balance is computed by the server across the whole history, not summed
 * from the visible rows — a figure that only counts the current page would look
 * authoritative and be wrong.
 */
export default function TransactionsTable({
  ownerType, ownerId, onGenerateStatement, statementDisabledReason, newLineCount,
}) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('');
  // Serialised as "type:id" so one <select> can offer tournaments, programs
  // and academies together without a second control.
  const [entityKey, setEntityKey] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [range, setRange] = useState(defaultRange);
  const [showExport, setShowExport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await paymentsAPI.getLedger({
        ownerType, ownerId, month: month || undefined,
        entity: parseEntity(entityKey),
        page,
      }));
    } catch (err) {
      toast.error(err?.message || 'Could not load transactions');
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId, month, entityKey, page]);

  useEffect(() => { load(); }, [load]);

  const [openingReceipt, setOpeningReceipt] = useState(null);

  /**
   * "Registration #328" is an opaque number until you can see what it was.
   * The receipt is built on demand and cached, so the first click is slightly
   * slower and every later one is immediate.
   */
  const openReceipt = async (line) => {
    // All of them: one payment can settle a batch, and a confirmation showing
    // only the first would under-report what was paid for.
    const id = String(line.registrationIds || '').trim();
    if (!id) return;
    const kind = line.programId ? 'program' : 'tournament';

    setOpeningReceipt(line.id);
    try {
      const { base64, contentType } = await paymentsAPI.getRegistrationReceipt(kind, id);
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
      if (!window.open(url, '_blank', 'noopener')) toast.error('Allow pop-ups to view confirmations');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.message || 'Could not open that confirmation');
    } finally {
      setOpeningReceipt(null);
    }
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const { csv, filename, lineCount } = await paymentsAPI.exportTransactions({
        ownerType, ownerId, from: range.from, to: range.to,
        entity: parseEntity(entityKey),
      });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${lineCount} transaction${lineCount === 1 ? '' : 's'} exported`);
      setShowExport(false);
    } catch (err) {
      toast.error(err?.message || 'Could not export');
    } finally {
      setExporting(false);
    }
  };

  const lines = data?.lines || [];

  return (
    <div className={`${theme.card} rounded-xl overflow-hidden`}>
      <div className={`flex flex-wrap items-center justify-between gap-3 p-4 border-b ${theme.border}`}>
        <div className="min-w-0">
          <h4 className={`text-sm font-bold ${theme.text}`}>Your transactions</h4>
          <p className={`text-xs ${theme.textMuted} mt-0.5`}>
            Entry fees Kynaix collected for you, and payments already made to you.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Shown whenever there is anything to narrow to. The threshold used
              to be "more than one entity", which quietly removed the control
              from an academy whose money came from a single tournament — the
              case where someone is most likely to go looking for it. */}
          {(data?.entities || []).length > 0 && (
            <select
              value={entityKey}
              onChange={(e) => { setEntityKey(e.target.value); setPage(1); }}
              className={`px-2.5 py-2 border ${theme.border} rounded-lg ${theme.input} text-xs max-w-[190px]`}
            >
              <option value="">All events</option>
              {data.entities.map(e => (
                // "No linked event" has no id, but it still needs a value of
                // its own — an empty one is the same value as "All events",
                // so choosing it silently showed everything.
                <option key={`${e.type}:${e.id}`} value={e.id ? `${e.type}:${e.id}` : e.type}>
                  {e.name} ({e.count})
                </option>
              ))}
            </select>
          )}

          <select
            value={month}
            onChange={(e) => { setMonth(e.target.value); setPage(1); }}
            className={`px-2.5 py-2 border ${theme.border} rounded-lg ${theme.input} text-xs`}
          >
            <option value="">All time</option>
            {(data?.months || []).map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>

          <button
            onClick={() => setShowExport(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} rounded-lg text-xs font-semibold`}
          >
            <Download size={13} /> Export
          </button>

          {onGenerateStatement && (
            <button
              onClick={onGenerateStatement}
              disabled={!!statementDisabledReason}
              title={statementDisabledReason || `Covers ${newLineCount} new transaction${newLineCount === 1 ? '' : 's'}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 ${theme.buttonPrimary} rounded-lg text-xs font-semibold disabled:opacity-40`}
            >
              <FileText size={13} />
              {newLineCount === 0 ? 'All statemented' : `Statement (${newLineCount})`}
            </button>
          )}
        </div>
      </div>

      {showExport && (
        <div className={`flex flex-wrap items-end gap-3 p-4 border-b ${theme.border} ${theme.backgroundSecondary}`}>
          {['from', 'to'].map(k => (
            <div key={k}>
              <label className={`block text-xs font-medium ${theme.textSecondary} mb-1 capitalize`}>{k}</label>
              <input
                type="date" value={range[k]}
                onChange={e => setRange(r => ({ ...r, [k]: e.target.value }))}
                className={`px-2.5 py-1.5 border ${theme.border} rounded-lg ${theme.input} text-xs`}
              />
            </div>
          ))}
          <button
            onClick={runExport}
            disabled={exporting}
            className={`px-3 py-2 ${theme.buttonPrimary} rounded-lg text-xs font-semibold disabled:opacity-50`}
          >
            {exporting ? 'Preparing…' : 'Download CSV'}
          </button>
          <p className={`text-xs ${theme.textMuted} self-center`}>Up to 3 months at a time.</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader size={18} className="animate-spin text-blue-500" /></div>
      ) : lines.length === 0 ? (
        <p className={`text-sm ${theme.textMuted} p-6 text-center`}>
          {month || entityKey ? 'Nothing matches these filters.' : 'No transactions yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={theme.backgroundSecondary}>
                {['Date', 'Description', 'Collected', 'Paid to you', 'Balance'].map((h, i) => (
                  <th key={h} className={`py-2.5 px-4 text-xs font-semibold ${theme.textSecondary} ${i > 1 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.id} className={`border-t ${theme.border}`}>
                  <td className={`py-2.5 px-4 ${theme.textSecondary} whitespace-nowrap`}>
                    {new Date(l.date).toLocaleDateString()}
                  </td>
                  <td className={`py-2.5 px-4 ${theme.text}`}>
                    {/* An entry fee links back to the registration that produced
                        it — otherwise "Registration #328" is an opaque number. */}
                    {l.type === 'collected' && l.registrationIds ? (
                      <button
                        onClick={() => openReceipt(l)}
                        disabled={openingReceipt === l.id}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-left disabled:opacity-50"
                        title="View the registration confirmation"
                      >
                        {openingReceipt === l.id ? 'Opening…' : l.description}
                      </button>
                    ) : l.description}
                    {l.invoiceNumber && (
                      <span className={`ml-2 text-xs ${theme.textMuted}`}>{l.invoiceNumber}</span>
                    )}
                  </td>
                  <td className={`py-2.5 px-4 text-right tabular-nums ${theme.text}`}>
                    {l.type === 'collected' ? money(l.amount) : '—'}
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-green-600">
                    {l.type === 'paid_out' ? money(l.amount) : '—'}
                  </td>
                  <td className={`py-2.5 px-4 text-right tabular-nums font-semibold ${theme.text}`}>
                    {money(l.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className={`flex items-center justify-between p-3 border-t ${theme.border}`}>
          <p className={`text-xs ${theme.textMuted}`}>
            {data.total} transaction{data.total === 1 ? '' : 's'} · page {data.page} of {data.totalPages}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(p - 1, 1))}
              disabled={data.page <= 1}
              className={`p-1.5 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-30`}
            ><ChevronLeft size={14} /></button>
            <button
              onClick={() => setPage(p => Math.min(p + 1, data.totalPages))}
              disabled={data.page >= data.totalPages}
              className={`p-1.5 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-30`}
            ><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
